import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import { toast } from '../../../../ui/toast';
import { Button } from '../../../../ui';
import {
  exportQuestions,
  generateQuestions,
  getJob,
} from '../../../../utils/api_service/job_api';
import { fetchSkills } from '../../../../utils/redux_store/features/skill_reducer';
import { fetchSubSkills } from '../../../../utils/redux_store/features/subskill_reducer';
import { store } from '../../../../utils/redux_store/store';
import {
  QuestionGeneratorJob,
  Questions,
} from '../../../../utils/types/content_manager_types';

type QuestionListProps = {
  job: QuestionGeneratorJob;
  questionsLoading: boolean;
  setJob: (newJobData: QuestionGeneratorJob) => void;
  errorJob: boolean;
};

export const QuestionsList: React.FC<QuestionListProps> = ({
  job,
  questionsLoading,
  setJob,
  errorJob,
}) => {
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [generateMoreLoading, setgenerateMoreLoading] =
    useState<boolean>(false);
  const [oldQuestionsCount, setOldQuestionsCount] = useState<number>(
    job.questions?.length || 0,
  );
  const [errorGenerateJob, setErrorGenerateJob] = useState<boolean>(false);
  const [questionImportList, setQuestionImportList] = useState<Array<string>>(
    [],
  );
  const allSelected = job.questions
    ?.filter((question) => question.exists === 'False')
    .every((question) => questionImportList.includes(question.questionId));

  const handleQuestionClick = (id: string) => {
    if (questionImportList.includes(id)) {
      setQuestionImportList(questionImportList.filter((item) => item !== id));
    } else {
      setQuestionImportList([...questionImportList, id]);
    }
  };

  const handleSelectAllClick = () => {
    if (allSelected) {
      setQuestionImportList([]);
    } else {
      const allQuestionIds =
        job.questions
          ?.filter((question) => question.exists === 'False')
          .map((question) => question.questionId) || [];
      setQuestionImportList(allQuestionIds);
    }
  };

  const pollJob = (id: string) => {
    getJob(id)
      .then((response) => {
        setJob(response);
        if (response.status && response.status.includes('Error')) {
          setErrorGenerateJob(true);
        } else {
          if (response.status !== 'Generation Completed...') {
            setTimeout(() => {
              pollJob(id);
            }, 5000);
          } else {
            setgenerateMoreLoading(false);
          }
        }
      })
      .catch(() => {
        toast.error('Error. Please try again', { position: 'bottom-center' });
        setErrorGenerateJob(true);
      });
  };

  const generateMore = () => {
    if (job.questions) {
      setOldQuestionsCount(job.questions?.length);
    }
    setgenerateMoreLoading(true);
    generateQuestions(job.id).then((response) => {
      if (response.ok) {
        pollJob(job.id);
      }
    });
  };

  const exportToQuestionBank = () => {
    setExportLoading(true);
    try {
      exportQuestions(job.id, questionImportList)
        .then((response) => {
          setJob(response);
          setQuestionImportList([]);
          store.dispatch(fetchSkills());
          store.dispatch(fetchSubSkills());
        })
        .catch(() => {
          toast.error('Error importing questions', {
            position: 'bottom-center',
          });
        });
    } catch {
      toast.error('Error importing questions', { position: 'bottom-center' });
    }
    setExportLoading(false);
  };

  const SkeletonLoader = () => (
    <Stack spacing={2}>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 800,
          color: errorJob ? 'error.main' : 'primary.main',
        }}
      >
        {generateMoreLoading && job.status === 'Generation Completed...'
          ? 'Generating More Questions...'
          : job.status}
      </Typography>
      <Stack spacing={2}>
        {[...new Array(5)].map((_, index) => (
          <Paper key={index} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Skeleton variant="text" width="100%" height={32} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="75%" height={32} />
          </Paper>
        ))}
      </Stack>
    </Stack>
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      {questionsLoading ? (
        <SkeletonLoader />
      ) : job.questions && job.questions.length > 0 ? (
        <>
          <Box
            sx={{
              mb: 2,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { sm: 'center' },
              justifyContent: 'space-between',
              gap: 1.5,
            }}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary' }}>
                Generated Questions
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Select new questions to import into the question bank.
              </Typography>
            </Box>
            <Button
              onClick={() => handleSelectAllClick()}
              variant="secondary"
              label={allSelected ? 'Unselect All' : 'Select All'}
            />
          </Box>
          <Stack spacing={1.5}>
            {job.questions.map((question: Questions) => {
              const isSelected = questionImportList.includes(
                question.questionId,
              );
              const newQuestion =
                parseInt(question.questionId) > oldQuestionsCount;
              const selectable = question.exists === 'False';

              return (
                <Paper
                  variant="outlined"
                  key={question.questionId}
                  onClick={() => {
                    if (selectable) {
                      handleQuestionClick(question.questionId);
                    }
                  }}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    cursor: selectable ? 'pointer' : 'default',
                    transition: 'all 0.2s',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    bgcolor: isSelected ? 'rgba(16, 185, 129, 0.05)' : 'background.paper',
                    '&:hover': selectable
                      ? { borderColor: isSelected ? 'primary.main' : 'text.disabled' }
                      : {},
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', lg: 'row' },
                      alignItems: { lg: 'flex-start' },
                      gap: 2,
                    }}
                  >
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={`#${question.questionId}`}
                          size="small"
                          sx={{ fontWeight: 700, fontSize: '0.6875rem', height: 22 }}
                        />
                        {newQuestion && (
                          <Chip
                            label="New"
                            size="small"
                            color="warning"
                            sx={{ fontWeight: 700, fontSize: '0.6875rem', height: 22 }}
                          />
                        )}
                        {question.exists === 'Exists' && (
                          <Chip
                            label="Already exists"
                            size="small"
                            color="error"
                            sx={{ fontWeight: 700, fontSize: '0.6875rem', height: 22 }}
                          />
                        )}
                        {question.exists === 'Added' && (
                          <Chip
                            label="Added"
                            size="small"
                            color="success"
                            sx={{ fontWeight: 700, fontSize: '0.6875rem', height: 22 }}
                          />
                        )}
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}
                        >
                          Question
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mt: 0.5 }}>
                          {question.question}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}
                        >
                          Answer
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, lineHeight: 1.6 }}>
                          {question.answer}
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        width: { lg: 288 },
                        minWidth: 0,
                      }}
                    >
                      {[question.skill, question.subSkill1, question.subSkill2, question.subSkill3]
                        .filter(Boolean)
                        .map((skill) => (
                          <Chip
                            key={skill}
                            label={skill}
                            size="small"
                            variant="outlined"
                            color="primary"
                            sx={{ fontWeight: 600, fontSize: '0.75rem', maxWidth: '100%' }}
                          />
                        ))}
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Stack>
          <Box
            sx={{
              mt: 2,
              pt: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { sm: 'center' },
              justifyContent: 'space-between',
              gap: 1.5,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              {questionImportList.length} questions selected
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
                onClick={() => generateMore()}
                variant="primary"
                label={generateMoreLoading ? 'Generating...' : 'Generate More'}
                disabled={job.questions.length > 29 || generateMoreLoading}
              />
              <Button
                onClick={() => exportToQuestionBank()}
                variant="primary"
                label={exportLoading ? 'Exporting...' : 'Add to Question Bank'}
                disabled={questionImportList.length === 0 || exportLoading}
              />
            </Stack>
          </Box>
          {generateMoreLoading && !errorGenerateJob && <SkeletonLoader />}
        </>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 120,
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'divider',
            bgcolor: 'action.hover',
            p: 3,
            textAlign: 'center',
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
            No generated questions yet
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', maxWidth: 400, mt: 0.5 }}>
            Questions will appear here when this content generation job completes.
          </Typography>
        </Box>
      )}
    </Paper>
  );
};
